import React, { ReactNode } from 'react';
import { z } from 'zod';
import { ModalsKeys, Nullable } from 'global';
import { useQuery } from '@apollo/client';
import { SelectField } from 'components/form';
import makeStyles from '@mui/styles/makeStyles';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, ReportProblem } from '@mui/icons-material';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { bpfAPI } from '../../../api';
import { BpfCloseReasonType } from '../../../types';
import { Q_VO } from '../../../../../queries/common';
import { useDealLifeCycle } from '../../../api/dealLifeCycle';
import { useModal } from '../../../../../hooks/useModal';

interface Props {
  parentId: string;
  stageId: string;
  templateId: string;
  isDealClosed: boolean;
  parentType: BpfLinkType;
  type: BpfCloseReasonType;
  onCancelCloseFlow: VoidFunction;
  closeReasonId: Nullable<string>;
  isOtherDepartmentsHaveUncompletedStages: { hasUncompleted: boolean; departmentNames: string[] };
}

const contentByType: Record<BpfCloseReasonType, { label: string; icon: ReactNode; notificationMessage: string }> = {
  [BpfCloseReasonType.Won]: {
    icon: <Info color="primary" />,
    label: 'Closed Won Reasons ',
    notificationMessage: 'You are completing the Deal. Please select a Closed Won Reason',
  },
  [BpfCloseReasonType.Lost]: {
    icon: <ReportProblem color="warning" />,
    label: 'Closed Lost Reasons ',
    notificationMessage: 'You are completing the Deal. Please select a Closed Lost Reason',
  },
};

const closeDealWithReasonFormSchema = z.object({
  parentId: z.string().uuid(),
  parentType: z.string(),
  stageId: z.string().uuid(),
  reasonId: z.string().uuid(),
});

type CloseDealWithReasonFormValues = z.infer<typeof closeDealWithReasonFormSchema>;

export const CloseDealWithReason = ({
  type,
  stageId,
  parentId,
  parentType,
  templateId,
  isDealClosed,
  closeReasonId,
  onCancelCloseFlow,
  isOtherDepartmentsHaveUncompletedStages,
}: Props) => {
  const { handleOpen } = useModal(ModalsKeys.Confirmation);
  const classes = useStyles();

  const { loading: dealLifeCycleLoading, actions: dealLifeCycleActions } = useDealLifeCycle();

  const { data: response, loading: closeReasonsLoading } = useQuery(bpfAPI.fetchCloseReasonsOptions, {
    notifyOnNetworkStatusChange: true,
    variables: { id: templateId, type: type },
  });

  const { control, handleSubmit } = useForm<CloseDealWithReasonFormValues>({
    resolver: zodResolver(closeDealWithReasonFormSchema),
    defaultValues: { reasonId: closeReasonId ?? '', parentId, stageId, parentType },
  });

  const currentReasonId = useWatch({ control, name: 'reasonId' });

  const submit = async (values: CloseDealWithReasonFormValues) => {
    if (isOtherDepartmentsHaveUncompletedStages.hasUncompleted) {
      handleOpen({
        title: 'Confirmation',
        message: `There are open business process flow steps for the ${isOtherDepartmentsHaveUncompletedStages.departmentNames.join(', ')} department. Are you sure you want to complete this deal now?`,
        onConfirm: async () => {
          await dealLifeCycleActions.handleCloseDeal(
            { ...values, reasonStatus: type === 'WON' ? 'SUCCESS' : 'FAILED' },
            () => ({
              awaitRefetchQueries: true,
              refetchQueries: [Q_VO],
            })
          );
        },
      });
    } else {
      await dealLifeCycleActions.handleCloseDeal(
        { ...values, reasonStatus: type === 'WON' ? 'SUCCESS' : 'FAILED' },
        () => ({
          awaitRefetchQueries: true,
          refetchQueries: [Q_VO],
        })
      );
    }

    onCancelCloseFlow();
  };

  return (
    <Box className={classes.container}>
      <Box className="notification">
        {contentByType[type].icon}

        <Typography variant="body1" color="textSecondary">
          {contentByType[type].notificationMessage}
        </Typography>
      </Box>

      <Box className="closeDealForm">
        <SelectField
          required
          name="reasonId"
          control={control}
          variant="outlined"
          isLoading={closeReasonsLoading}
          options={response?.bpf?.options}
          label={contentByType[type].label}
          disabled={!response?.bpf?.options?.length || isDealClosed || dealLifeCycleLoading.isDealClosing}
        />

        <RenderWithCondition condition={!isDealClosed}>
          <Stack direction="row" gap="0.5rem">
            <Button
              size="small"
              variant="contained"
              disabled={!currentReasonId}
              onClick={handleSubmit(submit)}
              loading={dealLifeCycleLoading.isDealClosing}
            >
              Confirm
            </Button>

            <Button size="small" onClick={onCancelCloseFlow}>
              Cancel
            </Button>
          </Stack>
        </RenderWithCondition>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '0.5rem',
    display: 'flex',
    flexDirection: 'column',

    '& .notification': {
      gap: '0.5rem',
      display: 'flex',
      direction: 'row',
      alignItems: 'center',
    },

    '& .closeDealForm': {
      gap: '1rem',
      display: 'grid',
      alignItems: 'center',
      gridTemplateColumns: 'auto 10rem',
    },
  },
});
